import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { GOOGLE_PAY_SDK_URL, NUVEI_SDK_URL } from '@/constants/nuvei';
import type { NuveiWalletPaymentResponse, SafeChargeFields, SafeChargeInstance } from '@/types/nuvei';
import {
  buildNuveiGooglePayAllowedPaymentMethods,
  buildNuveiGooglePayCreatePaymentRequest,
  isNuveiPaymentSessionFresh,
  isNuveiWalletPaymentCancelled,
  loadScriptOnce,
  mapNuveiGooglePayBillingAddress,
  resolveNuveiWalletConfig,
  type NuveiGooglePayBillingAddress,
} from '@/utils/nuvei';
import {
  getNuveiGooglePayPaymentRequestAmount,
  getNuveiChargeAmount,
  isNuveiGooglePayPaymentRequestAmountSupported,
} from '@/utils/pricing';
import { isEmailValid } from '@/utils/email';
import { logError } from '@/utils/logger';
import { shouldCollectZipCodeForTaxCalculation } from '@/utils/tax';
import { WalletButtonShell } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import type { NuveiWalletPaymentParams } from '@/types/paymentHandlers';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import { useNuveiWalletSettings } from '@/hooks/useNuveiWalletSettings';
import { useNuveiWalletLifecycle } from '@/hooks/useNuveiWalletLifecycle';
import { useLatestRef } from '@/hooks/useLatestRef';
import { isNuveiPendingResponse, isNuveiSuccessResponse } from '@/types/guards';

type NuveiGooglePayFormProps = ExpressCheckoutComponentProps;

type GooglePayPaymentData = {
  /** Present when the sheet was opened with `emailRequired: true`. */
  email?: string;
  paymentMethodData?: {
    info?: {
      billingAddress?: NuveiGooglePayBillingAddress;
    };
  };
};

type GooglePaymentsClient = {
  isReadyToPay: (request: unknown) => Promise<{ result: boolean }>;
  loadPaymentData: (request: unknown) => Promise<GooglePayPaymentData>;
  createButton: (options: {
    onClick: () => void;
    buttonType?: string;
    buttonColor?: string;
    buttonSizeMode?: 'static' | 'fill';
    buttonRadius?: number;
    buttonBorderType?: 'no_border' | 'default_border';
  }) => HTMLElement;
};

type GooglePaySessionData = {
  sessionToken: string;
  authJwt: string;
  merchantOrigin: string;
};

declare global {
  interface Window {
    google?: {
      payments?: {
        api?: {
          PaymentsClient: new (options: {
            environment: 'TEST' | 'PRODUCTION';
            merchantInfo?: { merchantId: string; merchantName: string };
            // ACDEV-25067: pay.js registers dynamic-price-update callbacks at
            // CLIENT construction (unlike Adyen's SDK which takes them in the
            // component config). Used by GooglePayDirectStripeForm's
            // wallet-ZIP tax flow; Nuvei doesn't collect ZIP in-sheet.
            paymentDataCallbacks?: {
              onPaymentDataChanged?: (intermediatePaymentData: unknown) => Promise<unknown>;
              onPaymentAuthorized?: (paymentData: unknown) => Promise<{ transactionState: 'SUCCESS' | 'ERROR' }>;
            };
          }) => GooglePaymentsClient;
        };
      };
    };
  }
}

/**
 * Nuvei Google Pay express/tile button.
 *
 * Do NOT use SafeCharge `createGooglePayPayment` when `merchantInfo` is set.
 * That SDK path never binds `this` before `createPayment`, so after Google
 * confirms it throws → catch → `{ cancelled: true }` and our finalize never runs.
 * We mirror the working SDK branch: `loadPaymentData` then `createPayment`.
 *
 * Session + merchant JWT are prefetched so click keeps Google's user gesture.
 * ZIP gating matches Nuvei Apple Pay / express overlay (US/CA zip only).
 */

export const NuveiGooglePayForm = ({ onInvalidTaxClick, onReady, isExpress }: NuveiGooglePayFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    paymentKeys,
    paymentMethodsSettings,
    pricing,
    customer,
    addressDetails,
    orderId,
    card: { cardElementRef, setCardElementRef },
    actions: { createSession, runPayment, setError, setIsExpressPaymentInProcess },
  } = context;

  const { providerSettings, nuveiConfig } = useNuveiWalletSettings(
    PaymentMethodEnum.GOOGLE_PAY,
    paymentKeys,
    paymentMethodsSettings
  );
  const nuveiCardMethod = useMemo(
    () =>
      paymentMethodsSettings.find(
        (method) => method.provider === ProviderEnum.NUVEI && method.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
      ),
    [paymentMethodsSettings]
  );
  const nuveiCardConfig = useMemo(
    () => resolveNuveiWalletConfig(paymentKeys.nuvei, nuveiCardMethod?.publicConfig),
    [paymentKeys.nuvei, nuveiCardMethod?.publicConfig]
  );
  const [paymentsClient, setPaymentsClient] = useState<GooglePaymentsClient | null>(null);
  const buttonHostRef = useRef<HTMLDivElement | null>(null);
  const contextRef = useLatestRef(context);
  const onInvalidTaxClickRef = useLatestRef(onInvalidTaxClick);
  const createSessionRef = useLatestRef(createSession);
  const runPaymentRef = useLatestRef(runPayment);
  const setErrorRef = useLatestRef(setError);
  const setIsExpressPaymentInProcessRef = useLatestRef(setIsExpressPaymentInProcess);
  const providerSettingsRef = useLatestRef(providerSettings);
  const nuveiConfigRef = useLatestRef(nuveiConfig);
  const nuveiCardConfigRef = useLatestRef(nuveiCardConfig);
  const pricingRef = useLatestRef(pricing);
  const customerRef = useLatestRef(customer);
  const addressDetailsRef = useLatestRef(addressDetails);
  const sessionBindingKey = JSON.stringify([pricing.priceBreakdown.priceTotal, pricing.currencyCode]);
  const sessionBindingKeyRef = useLatestRef(sessionBindingKey);
  // Session creation requires a valid `receipt_email` (the gateway 400s on a
  // partial/missing one). A missing email must NOT block the wallet: prefetch
  // is deferred (via `canCreateSession`) and the click opens the sheet, which
  // collects the buyer's email itself — Adyen/Stripe parity.
  const hasValidCustomerEmail = isEmailValid(customer.email);
  const hasValidCustomerEmailRef = useLatestRef(hasValidCustomerEmail);
  const isPaymentRequestSupported = useMemo(
    () => isNuveiGooglePayPaymentRequestAmountSupported(pricing.priceBreakdown.priceTotal, pricing),
    [pricing]
  );
  const orderIdRef = useLatestRef(orderId);
  const paymentsClientRef = useLatestRef(paymentsClient);

  const isProd = ['prod', 'live'].includes((nuveiConfig?.env || 'int').toLowerCase());
  const gatewayMerchantId = nuveiConfig?.gatewayMerchantId || '';

  const allowedPaymentMethods = useMemo(
    () => buildNuveiGooglePayAllowedPaymentMethods(gatewayMerchantId),
    [gatewayMerchantId]
  );
  const allowedPaymentMethodsRef = useLatestRef(allowedPaymentMethods);

  const fetchSessionAndJwt = useCallback(async (): Promise<GooglePaySessionData | null> => {
    const activeProvider = providerSettingsRef.current;
    if (!activeProvider) return null;
    const sessionResponse = await createSessionRef.current({
      paymentMethodId: activeProvider.id || '',
      email: customerRef.current.email || '',
    });
    const sessionToken = sessionResponse.sessionToken;
    const authJwt = sessionResponse.googlePay?.authJwt;
    const merchantOrigin = sessionResponse.googlePay?.merchantOrigin;
    if (!authJwt || !merchantOrigin) {
      throw new Error('Missing Nuvei Google Pay merchantInfo on payment session');
    }
    return {
      sessionToken,
      authJwt,
      merchantOrigin,
    };
  }, [createSessionRef, customerRef, providerSettingsRef]);

  // Post-sheet session creation for the no-email click path: by the time the
  // sheet resolved, the buyer's email is known (`emailRequired: true`), so the
  // openOrder session is created with it — the same "sheet first, charge
  // after" order Adyen/Stripe wallets use. Only `sessionToken` is needed at
  // this point (`authJwt`/`merchantOrigin` matter only before the sheet opens).
  const createSessionWithEmail = useCallback(
    async (email: string): Promise<{ sessionToken: string } | null> => {
      const activeProvider = providerSettingsRef.current;
      if (!activeProvider) return null;
      const sessionResponse = await createSessionRef.current({
        paymentMethodId: activeProvider.id || '',
        email,
      });
      return { sessionToken: sessionResponse.sessionToken };
    },
    [createSessionRef, providerSettingsRef]
  );

  const {
    state,
    setState,
    prefetchRef,
    prefetchSession: prefetchSessionAndJwt,
    refreshPaymentReadiness,
    isPaymentReady,
    buttonState,
    isPayingRef,
    settleAvailability,
    revealWallet,
  } = useNuveiWalletLifecycle({
    wallet: 'google_pay',
    bindingKey: sessionBindingKey,
    fetchSession: fetchSessionAndJwt,
    prefetchErrorMessage: 'Failed to prefetch Nuvei Google Pay session/JWT',
    isPaymentRequestSupported,
    canCreateSession: hasValidCustomerEmail,
    onReady,
  });

  useEffect(() => {
    // Wait for routed config / orderId — do not settle unavailable yet
    // (settleAvailability only allows one upgrade; premature false locks the wallet out).
    if (!providerSettings || !nuveiConfig) {
      setState('loading');
      return;
    }

    if (
      !nuveiConfig.merchantId ||
      !nuveiConfig.merchantSiteId ||
      !nuveiConfig.env ||
      !nuveiConfig.googleMerchantId ||
      !gatewayMerchantId ||
      !orderIdRef.current
    ) {
      logError('Nuvei Google Pay missing routed publicConfig', {
        paymentMethodId: providerSettings.id,
      });
      setState('loading');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        await Promise.all([
          loadScriptOnce(NUVEI_SDK_URL, 'nuvei-safecharge-sdk'),
          loadScriptOnce(GOOGLE_PAY_SDK_URL, 'google-pay-sdk'),
        ]);

        const PaymentsClient = window.google?.payments?.api?.PaymentsClient;
        if (!PaymentsClient) {
          throw new Error('Google Pay SDK unavailable');
        }

        const client = new PaymentsClient({ environment: isProd ? 'PRODUCTION' : 'TEST' });
        const ready = await client.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: allowedPaymentMethodsRef.current,
        });

        if (!ready.result) {
          if (!cancelled) {
            setState('unavailable');
            settleAvailability(false, 'unavailable');
          }
          return;
        }

        // Without a valid email the session prefetch is deferred; the button
        // still settles ready AND stays clickable — the Google sheet collects
        // the email and the session is created after it resolves (see onPay).
        if (hasValidCustomerEmailRef.current) {
          const prefetched = await prefetchSessionAndJwt();
          if (!prefetched) {
            throw new Error('Unable to prefetch Google Pay session');
          }
        }

        if (!cancelled) {
          setPaymentsClient(client);
          setState('ready');
          settleAvailability(true);
          revealWallet();
        }
      } catch (error) {
        logError('Failed to init Nuvei Google Pay', error);
        if (!cancelled) {
          setState('unavailable');
          settleAvailability(false, 'load_error');
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
    // Email changes must NOT re-init (that unmounts wallets) — the lifecycle's
    // `canCreateSession` resumes the session prefetch once an email is valid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    providerSettings?.id,
    nuveiConfig?.merchantId,
    nuveiConfig?.merchantSiteId,
    nuveiConfig?.googleMerchantId,
    gatewayMerchantId,
    orderId,
    isProd,
    settleAvailability,
    prefetchSessionAndJwt,
    revealWallet,
  ]);

  const restoreNuveiCardMerchant = useCallback(() => {
    try {
      const cardConfig = nuveiCardConfigRef.current;
      const googleConfig = nuveiConfigRef.current;
      if (
        !cardConfig?.merchantId ||
        !cardConfig.merchantSiteId ||
        (cardConfig.merchantId === googleConfig?.merchantId &&
          cardConfig.merchantSiteId === googleConfig?.merchantSiteId)
      ) {
        return;
      }
      const restoredSafeCharge = window.SafeCharge({
        merchantId: cardConfig.merchantId,
        merchantSiteId: cardConfig.merchantSiteId,
        env: cardConfig.env || 'int',
      });
      // SafeCharge() only mutates global SDK merchant state. Card tokenize/3DS still
      // read safeChargeInstance from cardElementRef — rebind it to the card MID instance.
      const currentFields = cardElementRef?.current as
        | (SafeChargeFields & { safeChargeInstance?: SafeChargeInstance | null })
        | null
        | undefined;
      if (currentFields) {
        currentFields.safeChargeInstance = restoredSafeCharge;
        setCardElementRef({ current: { ...currentFields, safeChargeInstance: restoredSafeCharge } });
      }
    } catch (error) {
      logError('Failed to restore Nuvei card merchant after Google Pay', error);
    }
  }, [cardElementRef, nuveiCardConfigRef, nuveiConfigRef, setCardElementRef]);

  const onPay = useCallback(() => {
    const activeClient = paymentsClientRef.current;
    const activeProvider = providerSettingsRef.current;
    const activeNuvei = nuveiConfigRef.current;
    if (!activeClient || !activeProvider || !activeNuvei || isPayingRef.current) {
      return;
    }

    const googleMerchantId = activeNuvei.googleMerchantId;
    const clientUniqueId = orderIdRef.current;
    if (!googleMerchantId || !clientUniqueId) {
      setErrorRef.current('Google Pay is not configured.');
      return;
    }

    // Nuvei does not collect ZIP in-sheet yet — always use on-page ZIP gate.
    const needsZip = shouldCollectZipCodeForTaxCalculation(
      customerRef.current,
      addressDetailsRef.current?.zipCode ?? customerRef.current.address?.zipCode,
      pricingRef.current.priceBreakdown.isTaxValid
    );
    if (needsZip) {
      onInvalidTaxClickRef.current?.();
      return;
    }

    const candidate = prefetchRef.current;
    const prefetched =
      candidate &&
      candidate.bindingKey === sessionBindingKeyRef.current &&
      isNuveiPaymentSessionFresh(candidate.createdAt)
        ? candidate
        : null;
    if (!prefetched && hasValidCustomerEmailRef.current) {
      // With a valid checkout email the session is prefetched out of band. Do
      // not await here — that would drop the user gesture Google needs to open
      // the sheet. The disabled button host blocks clicks until refresh completes.
      void refreshPaymentReadiness();
      return;
    }
    // No valid checkout email → no session could exist yet. Open the sheet
    // anyway: it collects the buyer's email (`emailRequired: true`) and the
    // openOrder session is created after the sheet resolves — the same
    // sheet-first order the Adyen/Stripe wallet flows use.

    let sheetAmount: string;
    let chargeAmount: string;
    try {
      sheetAmount = getNuveiGooglePayPaymentRequestAmount(
        pricingRef.current.priceBreakdown.priceTotal,
        pricingRef.current
      );
      // Must match Nuvei openOrder major amount — do not reuse the 2dp sheet string.
      chargeAmount = getNuveiChargeAmount(pricingRef.current.priceBreakdown.priceTotal, pricingRef.current);
    } catch (error) {
      logError('Nuvei Google Pay amount has unsupported precision', error);
      setErrorRef.current('Google Pay is unavailable for this amount.');
      setState('unavailable');
      settleAvailability(false, 'unavailable');
      return;
    }

    isPayingRef.current = true;
    setState('loading');
    // Consume token so a retry gets a fresh openOrder/JWT pair.
    prefetchRef.current = null;
    trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });

    const fallbackCountryCode = (
      customerRef.current.geoLocator?.countryCode2 ||
      customerRef.current.taxCountry ||
      'US'
    ).toUpperCase();
    const currencyCode = pricingRef.current.currencyCode.toUpperCase();

    const googlePaymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: allowedPaymentMethodsRef.current,
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: sheetAmount,
        currencyCode,
      },
      merchantInfo: {
        merchantId: googleMerchantId,
        merchantName: activeProvider.paymentMethodDisplayName || 'Store',
        // Session-bound iframe delegation (Nuvei-issued JWT for the publisher
        // origin). Only available when a session was prefetched; the no-email
        // click path opens the sheet with the plain merchant identity — the
        // same `merchantInfo` shape the direct Stripe/Adyen pay.js sheets use.
        ...(prefetched ? { merchantOrigin: prefetched.merchantOrigin, authJwt: prefetched.authJwt } : {}),
      },
      // No valid checkout email → the sheet collects it (same mechanism the
      // Adyen/direct-Stripe requests use) and it feeds `receipt_email`.
      // Skipped when the checkout already has a valid email so the sheet —
      // and the receipt/billing email — stay exactly as before.
      ...(hasValidCustomerEmailRef.current ? {} : { emailRequired: true }),
    };

    const finalizeFromSdk = (
      sdkResult: NuveiWalletPaymentResponse,
      { sessionToken, email }: { sessionToken: string; email: string }
    ) => {
      void (async () => {
        let shouldRefreshPaymentSession = true;
        try {
          if (isNuveiWalletPaymentCancelled(sdkResult)) {
            setIsExpressPaymentInProcessRef.current(false);
            return;
          }

          if (!sdkResult.transactionId) {
            logError('Nuvei Google Pay SDK returned no transaction to verify', sdkResult);
            setErrorRef.current(sdkResult.errorDescription || 'Google Pay payment failed');
            setIsExpressPaymentInProcessRef.current(false);
            return;
          }

          // Nuvei documents SDK responses as client-controlled. Confirm every
          // non-cancelled transaction server-side, including PENDING/DECLINED.
          const params: NuveiWalletPaymentParams = {
            sessionToken,
            transactionId: sdkResult.transactionId,
            email,
          };
          // Express path must force GOOGLE_PAY — selected tab is often card.
          // runPayment keeps/sets express loader for finalize + order polling.
          setIsExpressPaymentInProcessRef.current(true);
          const result = await runPaymentRef.current(params, PaymentMethodEnum.GOOGLE_PAY);
          shouldRefreshPaymentSession = !(
            isNuveiSuccessResponse(result, ProviderEnum.NUVEI) || isNuveiPendingResponse(result, ProviderEnum.NUVEI)
          );
        } catch (error) {
          logError('Nuvei Google Pay payment failed', error);
          setErrorRef.current('Google Pay payment failed');
          setIsExpressPaymentInProcessRef.current(false);
        } finally {
          isPayingRef.current = false;
          if (shouldRefreshPaymentSession) {
            void refreshPaymentReadiness();
          }
        }
      })();
    };

    // Start loadPaymentData synchronously from the click stack (Google gesture).
    // Then charge via createPayment — do not use createGooglePayPayment + merchantInfo
    // (SafeCharge bug: unbound `i.createPayment` → silent cancelled).
    void activeClient
      .loadPaymentData(googlePaymentDataRequest)
      .then(async (paymentData) => {
        if (!paymentData?.paymentMethodData) {
          throw new Error('Google Pay returned empty paymentMethodData');
        }
        notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });
        // Google sheet is closed — show the dark express loader immediately while
        // createPayment + finalize run (no 3DS path still has a multi-second gap).
        setIsExpressPaymentInProcessRef.current(true);

        // Sheet email wins (Adyen/Stripe precedence); checkout email is the fallback.
        const resolvedEmail = paymentData.email || customerRef.current.email || '';
        // Only `sessionToken` is needed from here on (`authJwt`/`merchantOrigin`
        // matter only before the sheet opens), so the post-sheet session's
        // narrower shape is fine.
        let sessionData: { sessionToken: string } | null = prefetched;
        if (!sessionData) {
          // Google guarantees `email` on the response when `emailRequired:
          // true` — guard anyway: the session gateway hard-rejects an invalid
          // `receipt_email`, and a specific error beats a generic 400 after
          // the shopper already authorized in the sheet.
          if (!isEmailValid(resolvedEmail)) {
            throw new Error('Google Pay sheet returned no usable email for session creation');
          }
          sessionData = await createSessionWithEmail(resolvedEmail);
        }
        if (!sessionData) {
          throw new Error('Unable to create Google Pay session');
        }

        const billingAddress = mapNuveiGooglePayBillingAddress(paymentData.paymentMethodData.info?.billingAddress, {
          email: resolvedEmail,
          country: fallbackCountryCode,
        });
        const billingCountryCode = (billingAddress.country || fallbackCountryCode).toUpperCase();

        // SafeCharge mutates shared SDK merchant state. Creating the Google MID
        // while the express button mounts overwrites concurrently mounted Nuvei
        // card fields that may use a different routed MID.
        const activeSafeCharge = window.SafeCharge({
          merchantId: activeNuvei.merchantId,
          merchantSiteId: activeNuvei.merchantSiteId,
          env: activeNuvei.env || 'int',
        });
        let cardMerchantRestored = false;
        const restoreCardMerchantOnce = () => {
          if (cardMerchantRestored) return;
          cardMerchantRestored = true;
          try {
            restoreNuveiCardMerchant();
          } catch (restoreError) {
            logError('Failed to restore Nuvei card merchant after Google Pay', restoreError);
          }
        };
        // Safety net: if createPayment never invokes its callback, restore the card
        // MID and unblock checkout (clear paying/loader state) so the user can retry.
        // A charge that actually went through is still resolved server-side via DMN.
        let sdkCallbackSettled = false;
        const restoreSafetyTimer = window.setTimeout(() => {
          restoreCardMerchantOnce();
          if (sdkCallbackSettled) return;
          sdkCallbackSettled = true;
          logError('Nuvei Google Pay createPayment callback timed out', { orderId: clientUniqueId });
          isPayingRef.current = false;
          setErrorRef.current('Google Pay payment failed');
          setIsExpressPaymentInProcessRef.current(false);
          void refreshPaymentReadiness();
        }, 15_000);
        activeSafeCharge.createPayment(
          buildNuveiGooglePayCreatePaymentRequest({
            sessionToken: sessionData.sessionToken,
            merchantId: activeNuvei.merchantId,
            merchantSiteId: activeNuvei.merchantSiteId,
            clientUniqueId,
            amount: chargeAmount,
            currency: currencyCode,
            googlePay3Dflow: activeNuvei.googlePay3Dflow || 'enable',
            billingAddress: {
              ...billingAddress,
              country: billingCountryCode,
            },
            email: resolvedEmail,
            country: billingCountryCode,
            mobileToken: JSON.stringify(paymentData.paymentMethodData),
          }),
          (response: unknown) => {
            window.clearTimeout(restoreSafetyTimer);
            restoreCardMerchantOnce();
            if (sdkCallbackSettled) {
              // Timed out and already recovered — a retry may be in flight; the
              // backend confirm/DMN remains the source of truth for this charge.
              logError('Nuvei Google Pay createPayment callback arrived after timeout', response);
              return;
            }
            sdkCallbackSettled = true;
            finalizeFromSdk(response as NuveiWalletPaymentResponse, {
              sessionToken: sessionData.sessionToken,
              email: resolvedEmail,
            });
          }
        );
      })
      .catch((error: { statusCode?: string } | Error) => {
        try {
          restoreNuveiCardMerchant();
        } catch (restoreError) {
          logError('Failed to restore Nuvei card merchant after Google Pay cancel/error', restoreError);
        }
        isPayingRef.current = false;
        const statusCode = typeof error === 'object' && error && 'statusCode' in error ? error.statusCode : undefined;
        if (statusCode === 'CANCELED' || statusCode === 'USER_CANCELED') {
          setIsExpressPaymentInProcessRef.current(false);
          void refreshPaymentReadiness();
          return;
        }
        logError('Nuvei Google Pay loadPaymentData/createPayment failed', error);
        setErrorRef.current('Google Pay payment failed');
        setIsExpressPaymentInProcessRef.current(false);
        void refreshPaymentReadiness();
      });
  }, [
    addressDetailsRef,
    allowedPaymentMethodsRef,
    contextRef,
    createSessionWithEmail,
    customerRef,
    hasValidCustomerEmailRef,
    isPayingRef,
    nuveiConfigRef,
    onInvalidTaxClickRef,
    orderIdRef,
    paymentsClientRef,
    prefetchRef,
    pricingRef,
    providerSettingsRef,
    refreshPaymentReadiness,
    restoreNuveiCardMerchant,
    runPaymentRef,
    sessionBindingKeyRef,
    setErrorRef,
    setIsExpressPaymentInProcessRef,
    setState,
    settleAvailability,
  ]);

  const onPayRef = useLatestRef(onPay);

  useEffect(() => {
    if (state !== 'ready' || !paymentsClient || !buttonHostRef.current) {
      return;
    }
    buttonHostRef.current.innerHTML = '';
    const button = paymentsClient.createButton({
      onClick: () => {
        onPayRef.current();
      },
      buttonType: 'plain',
      buttonColor: 'black',
      buttonSizeMode: 'fill',
      buttonRadius: 8,
      buttonBorderType: 'no_border',
    });
    button.style.width = '100%';
    button.style.height = '100%';
    buttonHostRef.current.appendChild(button);
  }, [onPayRef, paymentsClient, state]);

  // No valid checkout email → the session prefetch is deferred, but the button
  // must stay CLICKABLE (Adyen/Stripe parity): the sheet collects the email and
  // the session is created after it resolves. With a valid email, keep the
  // prefetch-freshness gate so a click always has a session ready.
  const isButtonInteractive = isPaymentReady || !hasValidCustomerEmail;

  // `paymentsClient`/`state` are deps so the sync also covers a button created
  // while payment is not yet ready (e.g. session prefetch deferred on email).
  useEffect(() => {
    const button = buttonHostRef.current?.querySelector<HTMLButtonElement>('button');
    if (button) {
      button.disabled = !isButtonInteractive;
    }
  }, [isButtonInteractive, paymentsClient, state]);

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  return (
    <WalletButtonShell
      state={buttonState}
      placeholder={<GooglePayButtonPlaceholder />}
      testId="wallet-btn-google-nuvei"
      usePlaceholderAsVisual
    >
      <div
        ref={buttonHostRef}
        className={`w-full h-12 min-h-12 rounded-lg overflow-hidden ${isButtonInteractive ? '' : 'pointer-events-none'}`}
        data-testid="nuvei-google-pay-button"
        aria-disabled={!isButtonInteractive || undefined}
      />
    </WalletButtonShell>
  );
};
